import { createSlice } from "@reduxjs/toolkit";

import { getIpos } from "../actions/ipos/getIpos";
import { getIpo } from "../actions/ipos/getIpo";
import { createIpo } from "../actions/ipos/createIpo";
import { updateIpo } from "../actions/ipos/updateIpo";
import { deleteIpo } from "../actions/ipos/deleteIpo";
import { changeIpoStatus } from "../actions/ipos/changeIpoStatus";
import { getIpoSubscriptions } from "../actions/ipos/getIpoSubscriptions";
import { allotIpo } from "../actions/ipos/allotIpo";
import { goLiveIpo } from "../actions/ipos/goLiveIpo";
import { refundIpoSubscription } from "../actions/ipos/refundIpoSubscription";
import { calculateIpoForUser } from "../actions/ipos/calculateIpoForUser";
import { subscribeIpoForUser } from "../actions/ipos/subscribeIpoForUser";

export const iposSlice = createSlice({
  name: "ipos",
  initialState: {
    error: null,
    allIpos: [],
    allIposInfo: null,
    // GET /{id} returns the offer plus subscription totals; the totals key is
    // read defensively in the page since the doc doesn't pin its name.
    ipo: null,
    ipoDetail: null,
    ipoSubscriptions: [],
    ipoSubscriptionsSummary: null,
    ipoSubscriptionsInfo: null,
    allotmentResult: null,
    // { ipo, user, summary } from calculate-for-user
    ipoQuote: null,
    getIposPending: false,
    getIpoPending: false,
    subscriptionsPending: false,
    createPending: false,
    updatePending: false,
    deletePending: false,
    statusPending: false,
    allotPending: false,
    goLivePending: false,
    refundPending: false,
    quotePending: false,
    subscribeForUserPending: false,
  },

  reducers: {
    clearAllotmentResult: (state) => {
      state.allotmentResult = null;
    },
    clearIpoQuote: (state) => {
      state.ipoQuote = null;
    },
  },

  extraReducers: (builder) => {
    // List offers
    builder.addCase(getIpos.pending, (state) => {
      state.getIposPending = true;
    });
    builder.addCase(getIpos.fulfilled, (state, action) => {
      state.getIposPending = false;
      state.allIposInfo = action.payload;
      state.allIpos = action.payload?.ipos || action.payload?.data || [];
    });
    builder.addCase(getIpos.rejected, (state, action) => {
      state.getIposPending = false;
      state.error = action.error.message;
    });

    // Single offer
    builder.addCase(getIpo.pending, (state) => {
      state.getIpoPending = true;
    });
    builder.addCase(getIpo.fulfilled, (state, action) => {
      state.getIpoPending = false;
      state.ipoDetail = action.payload;
      state.ipo = action.payload?.ipo || action.payload;
    });
    builder.addCase(getIpo.rejected, (state, action) => {
      state.getIpoPending = false;
      state.error = action.error.message;
    });

    // Subscribers
    builder.addCase(getIpoSubscriptions.pending, (state) => {
      state.subscriptionsPending = true;
    });
    builder.addCase(getIpoSubscriptions.fulfilled, (state, action) => {
      state.subscriptionsPending = false;
      state.ipoSubscriptionsInfo = action.payload;
      state.ipoSubscriptions =
        action.payload?.subscriptions || action.payload?.data || [];
      state.ipoSubscriptionsSummary = action.payload?.summary || null;
    });
    builder.addCase(getIpoSubscriptions.rejected, (state, action) => {
      state.subscriptionsPending = false;
      state.error = action.error.message;
    });

    // Create
    builder.addCase(createIpo.pending, (state) => {
      state.createPending = true;
    });
    builder.addCase(createIpo.fulfilled, (state) => {
      state.createPending = false;
    });
    builder.addCase(createIpo.rejected, (state, action) => {
      state.createPending = false;
      state.error = action.error.message;
    });

    // Update
    builder.addCase(updateIpo.pending, (state) => {
      state.updatePending = true;
    });
    builder.addCase(updateIpo.fulfilled, (state, action) => {
      state.updatePending = false;
      if (action.payload?.ipo) {
        state.ipo = action.payload.ipo;
      }
    });
    builder.addCase(updateIpo.rejected, (state, action) => {
      state.updatePending = false;
      state.error = action.error.message;
    });

    // Delete
    builder.addCase(deleteIpo.pending, (state) => {
      state.deletePending = true;
    });
    builder.addCase(deleteIpo.fulfilled, (state) => {
      state.deletePending = false;
      state.ipo = null;
      state.ipoDetail = null;
    });
    builder.addCase(deleteIpo.rejected, (state, action) => {
      state.deletePending = false;
      state.error = action.error.message;
    });

    // Status change
    builder.addCase(changeIpoStatus.pending, (state) => {
      state.statusPending = true;
    });
    builder.addCase(changeIpoStatus.fulfilled, (state, action) => {
      state.statusPending = false;
      if (action.payload?.ipo) {
        state.ipo = action.payload.ipo;
      }
    });
    builder.addCase(changeIpoStatus.rejected, (state, action) => {
      state.statusPending = false;
      state.error = action.error.message;
    });

    // Allotment
    builder.addCase(allotIpo.pending, (state) => {
      state.allotPending = true;
      state.allotmentResult = null;
    });
    builder.addCase(allotIpo.fulfilled, (state, action) => {
      state.allotPending = false;
      state.allotmentResult = action.payload;
      if (action.payload?.ipo) {
        state.ipo = action.payload.ipo;
      }
    });
    builder.addCase(allotIpo.rejected, (state, action) => {
      state.allotPending = false;
      state.error = action.error.message;
    });

    // Go live
    builder.addCase(goLiveIpo.pending, (state) => {
      state.goLivePending = true;
    });
    builder.addCase(goLiveIpo.fulfilled, (state, action) => {
      state.goLivePending = false;
      if (action.payload?.ipo) {
        state.ipo = action.payload.ipo;
      }
    });
    builder.addCase(goLiveIpo.rejected, (state, action) => {
      state.goLivePending = false;
      state.error = action.error.message;
    });

    // Refund a subscription
    builder.addCase(refundIpoSubscription.pending, (state) => {
      state.refundPending = true;
    });
    builder.addCase(refundIpoSubscription.fulfilled, (state) => {
      state.refundPending = false;
    });
    builder.addCase(refundIpoSubscription.rejected, (state, action) => {
      state.refundPending = false;
      state.error = action.error.message;
    });

    // Quote a subscription on behalf of a user
    builder.addCase(calculateIpoForUser.pending, (state) => {
      state.quotePending = true;
    });
    builder.addCase(calculateIpoForUser.fulfilled, (state, action) => {
      state.quotePending = false;
      state.ipoQuote = action.payload;
    });
    builder.addCase(calculateIpoForUser.rejected, (state, action) => {
      state.quotePending = false;
      state.ipoQuote = null;
      state.error = action.error.message;
    });

    // Subscribe on behalf of a user
    builder.addCase(subscribeIpoForUser.pending, (state) => {
      state.subscribeForUserPending = true;
    });
    builder.addCase(subscribeIpoForUser.fulfilled, (state) => {
      state.subscribeForUserPending = false;
      state.ipoQuote = null;
    });
    builder.addCase(subscribeIpoForUser.rejected, (state, action) => {
      state.subscribeForUserPending = false;
      state.error = action.error.message;
    });
  },
});

export const { clearAllotmentResult, clearIpoQuote } = iposSlice.actions;

export default iposSlice.reducer;
